/* eslint-disable no-console */
import {Button, Popconfirm, Table as TableAnt, Form} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import {NamePath, StoreValue} from 'antd/es/form/interface';
import moment from 'moment';
import React, {useContext, useEffect, useState} from 'react';
import ReactQuill from 'react-quill';
import {useSearchParams} from 'react-router-dom';

import {
  GOODS_CATEGORY_OPTIONS,
  ROUTE_OPTIONS,
  getRouteOptionsForGoodsCategory
} from '../../pages/esgManager/constants/cbam/IronandSteel/ironAndSteelOptions';
import {processSetupKeys} from '../../pages/esgManager/constants/cbam/IronandSteel/ironandSteelQuestionsKeys';
import {
  isFieldVisible,
  isFieldDisabled,
  isFieldRequired
} from '../../pages/esgManager/constants/commonInputFormFunction';
import SubmitDisabledContext from '../../pages/esgManager/constants/SubmitDisabledContext';
import {inputType} from '../../pages/esgManager/interface/inputType';
import QuestionnaireSwitch from '../../pages/esgManager/utils/QuestionnaireSwitch';
import {replaceCurrentAndPrevious} from '../../shared/checkSelectedYear';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {formatIndian} from '../../utils/formatIndian';
import {Units} from '../Forms/Constants/CommonESGConstants/constants';
import {evaluateExpression} from '../Forms/Utils/expressionEvaluator';

import {Title} from './Constants/Common';
import {
  RouteFieldWithDynamicOptions,
  getDynamicOptions,
  DynamicTableSync
} from './Utils/DynamicTableSync';
import ModalAddNewToTable from './Utils/ModalAddNewToTable';
import RouteFieldWithFilteredOptions from './Utils/RouteFieldWithFilteredOptions';

const QuestionnaireTable: React.FC<{
  field: any;
  getFieldValue: (name: NamePath) => StoreValue;
}> = ({field, getFieldValue}) => {
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<any[]>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);

  const [searchParams] = useSearchParams();
  const selectedYear = searchParams.get('selectedYear');

  const setIsSubmitDisabled = useContext(SubmitDisabledContext);

  const FI = useFormInstance();
  const {setFieldsValue} = FI;

  // Check if this is table B that needs to sync from table A
  const isTableB =
    field.fieldName === processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_B;

  // Initialize dataSource from form values
  useEffect(() => {
    setDataSource(getFieldValue([field.fieldName]) ?? []);
  }, []);

  const handleDelete = (_id: string) => {
    const updatedDataSource = dataSource.filter((item) => item._id !== _id);
    setDataSource(updatedDataSource);
    setFieldsValue({[field.fieldName]: updatedDataSource});
    setIsSubmitDisabled(false);
  };

  const srNoColumn = [
    {
      title: 'S. No.',
      dataIndex: 'srNo',
      render: (id: any, record: any, index: any) => {
        const serialNumber = (page - 1) * pageSize + ++index;
        const prefix = field?.srNoPrefix || '';

        return prefix ? `${prefix}${serialNumber}` : serialNumber;
      },
      width: 'fit-content'
    }
  ];

  const column = [
    {
      title: Title.actionTitle,
      dataIndex: 'operation',
      fixed: 'right',
      width: '100px',
      render: (value: any, record: any) => {
        return dataSource.length >= 1 ? (
          <>
            <Button
              onClick={() => {
                setShowModel(true);
                setEditData(record);
                setIsEdit(true);
                setEditId(record._id);
              }}
              type="link"
              style={{marginRight: '20px', padding: '4px 0px'}}
            >
              Edit
            </Button>
            {!isTableB && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  handleDelete(record._id);
                }}
              >
                <Button type="link">Delete</Button>
              </Popconfirm>
            )}
          </>
        ) : null;
      }
    }
  ];

  const addedColumns = field?.formList?.formFields.map((field: any) => ({
    title: replaceCurrentAndPrevious(field.fieldLabel, selectedYear ?? ''),
    dataIndex: field.fieldName,
    render: (value: any) => {
      if (field.type === inputType.DATEPICKER) {
        return value
          ? moment(value).format(field?.dateFormat || 'DD-MM-YYYY')
          : '-';
      }
      if (field.type === inputType.CITY) {
        return value
          ? cityOptions.find((item) => item.value === value)?.label
          : '-';
      }
      if (field.type === inputType.STATE) {
        return value
          ? stateOptions.find((item) => item.value === value)?.label
          : '-';
      }
      if (field.type === inputType.PERCENTAGE) {
        return value ? `${value}${Units.percentage}` : '-';
      }
      if (field.type === inputType.NUMBER) {
        return value ? formatIndian(value) : '-';
      }
      if (field.type === inputType.FILEUPLOAD) {
        if (!Array.isArray(value) || value.length === 0) {
          return '-';
        }

        return value.map((file: any) => (
          <div key={file.uid}>
            {file.url ? (
              <a href={file.url} target="_blank" rel="noreferrer">
                {file.name}
              </a>
            ) : (
              file.name
            )}
          </div>
        ));
      }
      if (field.type === inputType.WYSIWYG) {
        return value ? (
          <ReactQuill
            className="quill-table-cell"
            value={value}
            readOnly={true}
            theme={'bubble'}
          />
        ) : (
          '-'
        );
      }
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '-';
      }

      return value || '-';
    }
  }));

  const columns = [...srNoColumn, ...addedColumns, ...column];

  const allowMultipleRows = field.allowMultipleRows !== false;
  const canAddNew = allowMultipleRows || dataSource.length === 0;

  const goodsFieldNameForRoutes = React.useMemo(() => {
    const formFields = field?.formList?.formFields || [];
    const goodsField = formFields.find(
      (f: any) =>
        f.type === inputType.SELECT && f.options === GOODS_CATEGORY_OPTIONS
    );

    return goodsField?.fieldName;
  }, [field]);

  const isRouteField = (formField: any): boolean =>
    !!goodsFieldNameForRoutes &&
    formField?.type === inputType.SELECT &&
    formField?.options === ROUTE_OPTIONS;

  const getFilteredRouteOptions = (
    selectedGoodsCategory: string | undefined,
    allRouteOptions: any[]
  ): any[] => {
    // Delegate to shared helper so this component stays generic.
    // Helper already uses GOODS_CATEGORY_OPTIONS and ROUTE_OPTIONS internally.
    if (!allRouteOptions || allRouteOptions.length === 0) {
      return allRouteOptions;
    }

    return getRouteOptionsForGoodsCategory(selectedGoodsCategory);
  };

  // Wrap table B in Form.Item to watch table A changes
  if (isTableB) {
    return (
      <>
        <DynamicTableSync
          fieldName={field.fieldName}
          dataSource={dataSource}
          setDataSource={setDataSource}
          isTableB={isTableB}
        />
        <TableAnt
          className="questionnaire-table"
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'max-content'}}
          tableLayout="auto"
          pagination={{
            pageSize,
            onChange(current) {
              setPage(current);
            }
          }}
        />
        <ModalAddNewToTable
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          field={field}
          data={isEdit ? editData : undefined}
          FormComponent={field?.formList?.formFields.map(
            (formField: any, index: any) => {
              // Handle dynamic options from table A (for Route fields in Section 1.6.1 B)
              if (formField?.dynamicOptionsFromTableA && isTableB) {
                return (
                  <RouteFieldWithDynamicOptions
                    key={index}
                    formField={formField}
                    parentFormInstance={FI}
                    getDynamicOptions={getDynamicOptions}
                    selectedYear={selectedYear}
                    replaceCurrentAndPrevious={replaceCurrentAndPrevious}
                  />
                );
              }

              // Check conditionalVisibility - if field should be hidden, don't render it
              if (
                formField?.conditionalVisibility ||
                formField?.conditions?.shouldShow
              ) {
                return (
                  <Form.Item key={index} noStyle shouldUpdate>
                    {({getFieldsValue}) => {
                      const allFields = getFieldsValue();
                      // Create a getFieldValue wrapper that uses current form data
                      const getFieldValueWrapper = (
                        fieldName: string | string[]
                      ) => {
                        if (typeof fieldName === 'string') {
                          return allFields[fieldName];
                        }
                        // Handle array path like ['field', 'subField']
                        let value = allFields;
                        for (const key of fieldName) {
                          value = value?.[key];
                        }

                        return value;
                      };

                      let shouldShow = true;

                      if (formField?.conditionalVisibility) {
                        shouldShow = isFieldVisible(
                          {
                            conditionalVisibility:
                              formField.conditionalVisibility
                          } as any,
                          getFieldValueWrapper
                        );
                      }

                      if (formField?.conditions?.shouldShow) {
                        shouldShow = evaluateExpression(
                          formField?.conditions?.shouldShow.expression,
                          formField?.conditions?.shouldShow
                            ?.formDataDependOn === 'PARENT_FORM'
                            ? FI.getFieldValue([])
                            : getFieldValue([])
                        );
                      }

                      if (!shouldShow) return null;

                      // Get dynamic options if needed
                      let options = formField.options;
                      if (formField?.dynamicOptionsFromTableA && isTableB) {
                        const tableAData = (FI.getFieldValue([
                          processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
                        ]) ?? []) as any[];
                        options = getDynamicOptions(formField, tableAData);
                      }

                      return (
                        <QuestionnaireSwitch
                          field={{
                            fieldType: formField.type,
                            fieldLabel: replaceCurrentAndPrevious(
                              formField.fieldLabel,
                              selectedYear ?? ''
                            ),
                            textLimit: formField.textLimit,
                            options,
                            heading: replaceCurrentAndPrevious(
                              formField.heading,
                              selectedYear ?? ''
                            ),
                            colSpan: formField.colSpan,
                            restrictFutureDateSelection:
                              formField.restrictFutureDateSelection,
                            fieldName: formField.fieldName,
                            units: formField.units,
                            dateFormat: formField.dateFormat,
                            relatedKey: formField.relatedKey,
                            disabled: formField.disabled,
                            disabledOn: formField.disabledOn,
                            requiredOn: formField.requiredOn,
                            fileUploadAction: formField.fileUploadAction,
                            fileUploadValidExtensions:
                              formField.fileUploadValidExtensions,
                            fileUploadMaxSize: formField.fileUploadMaxSize,
                            fileUploadAllowMultipleFiles:
                              formField.fileUploadAllowMultipleFiles,
                            conditions: formField.conditions
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                );
              }

              // If the field has disabledOn or requiredOn logic, wrap it with Form.Item dependencies
              if (
                formField?.disabledOn ||
                formField?.requiredOn ||
                formField?.conditions?.shouldDisable
              ) {
                const dependencies: string[] = [];
                if (formField?.disabledOn) {
                  dependencies.push(formField.disabledOn.fieldName);
                }
                if (formField?.requiredOn) {
                  dependencies.push(formField.requiredOn.fieldName);
                }
                // Add table A dependency if field needs dynamic options
                if (formField?.dynamicOptionsFromTableA && isTableB) {
                  dependencies.push(
                    processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
                  );
                }

                return (
                  <Form.Item
                    key={index}
                    noStyle
                    // Re-render whenever a dependency changes, including when it is
                    // set programmatically (e.g. WYSIWYG setFieldValue or loading a
                    // row for edit), which the `dependencies` prop does not react to
                    shouldUpdate={(prev, cur) =>
                      dependencies.some((dep) => prev?.[dep] !== cur?.[dep])
                    }
                  >
                    {({
                      getFieldValue
                    }: {
                      getFieldValue: (name: NamePath) => StoreValue;
                    }) => {
                      // Create a wrapper function for getFieldValue to match the expected signature
                      const getFieldValueWrapper = (
                        fieldName: string | string[]
                      ) => {
                        if (typeof fieldName === 'string') {
                          return getFieldValue(fieldName);
                        }
                        // Handle array path

                        return getFieldValue(fieldName);
                      };

                      // Get dynamic options if needed
                      let options = formField.options;
                      if (formField?.dynamicOptionsFromTableA && isTableB) {
                        const tableAData = (getFieldValue(
                          processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
                        ) ?? []) as any[];
                        options = getDynamicOptions(formField, tableAData);
                      }

                      let isDisabled = false;
                      if (formField?.disabledOn) {
                        isDisabled = isFieldDisabled(
                          formField,
                          getFieldValueWrapper,
                          formField.rowId,
                          FI.getFieldValue([formField?.disabledOn?.fieldName])
                        );
                      }
                      const isRequired = isFieldRequired(
                        formField,
                        getFieldValueWrapper,
                        formField.rowId
                      );

                      if (formField?.conditions?.shouldDisable) {
                        isDisabled = evaluateExpression(
                          formField?.conditions?.shouldDisable.expression,
                          formField?.conditions?.shouldDisable
                            ?.formDataDependOn === 'PARENT_FORM'
                            ? FI.getFieldValue([])
                            : getFieldValue([])
                        );
                      }

                      return (
                        <QuestionnaireSwitch
                          field={{
                            fieldType: formField.type,
                            fieldLabel: replaceCurrentAndPrevious(
                              formField.fieldLabel,
                              selectedYear ?? ''
                            ),
                            textLimit: formField.textLimit,
                            options: options,
                            heading: replaceCurrentAndPrevious(
                              formField.heading,
                              selectedYear ?? ''
                            ),
                            colSpan: formField.colSpan,
                            restrictFutureDateSelection:
                              formField.restrictFutureDateSelection,
                            fieldName: formField.fieldName,
                            units: formField.units,
                            dateFormat: formField.dateFormat,
                            relatedKey: formField.relatedKey,
                            formula: formField.formula,
                            roundTo: formField.roundTo,
                            disabled: isDisabled,
                            disabledOn: formField.disabledOn,
                            requiredOn: formField.requiredOn,
                            isRequired: isRequired,
                            fileUploadAction: formField.fileUploadAction,
                            fileUploadValidExtensions:
                              formField.fileUploadValidExtensions,
                            fileUploadMaxSize: formField.fileUploadMaxSize,
                            fileUploadAllowMultipleFiles:
                              formField.fileUploadAllowMultipleFiles,
                            conditions: formField.conditions
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                );
              }

              // For fields without disabledOn logic, render normally
              // But still check for dynamic options
              let finalOptions = formField.options;
              if (formField?.dynamicOptionsFromTableA && isTableB) {
                // Wrap in Form.Item to watch table A changes
                return (
                  <Form.Item
                    key={index}
                    noStyle
                    dependencies={[
                      processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
                    ]}
                  >
                    {({getFieldValue}) => {
                      const tableAData = (getFieldValue(
                        processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
                      ) ?? []) as any[];
                      finalOptions = getDynamicOptions(formField, tableAData);

                      return (
                        <QuestionnaireSwitch
                          field={{
                            fieldType: formField.type,
                            fieldLabel: replaceCurrentAndPrevious(
                              formField.fieldLabel,
                              selectedYear ?? ''
                            ),
                            textLimit: formField.textLimit,
                            options: finalOptions,
                            heading: replaceCurrentAndPrevious(
                              formField.heading,
                              selectedYear ?? ''
                            ),
                            colSpan: formField.colSpan,
                            restrictFutureDateSelection:
                              formField.restrictFutureDateSelection,
                            fieldName: formField.fieldName,
                            units: formField.units,
                            dateFormat: formField.dateFormat,
                            relatedKey: formField.relatedKey,
                            formula: formField.formula,
                            roundTo: formField.roundTo,
                            disabled: formField.disabled,
                            disabledOn: formField.disabledOn,
                            fileUploadAction: formField.fileUploadAction,
                            fileUploadValidExtensions:
                              formField.fileUploadValidExtensions,
                            fileUploadMaxSize: formField.fileUploadMaxSize,
                            fileUploadAllowMultipleFiles:
                              formField.fileUploadAllowMultipleFiles,
                            conditions: formField.conditions,
                            requiredOn: formField.requiredOn
                              ? {
                                  ...formField.requiredOn,
                                  fieldName: formField.requiredOn.fieldName
                                }
                              : undefined
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                );
              }

              return (
                <QuestionnaireSwitch
                  field={{
                    fieldType: formField.type,
                    fieldLabel: replaceCurrentAndPrevious(
                      formField.fieldLabel,
                      selectedYear ?? ''
                    ),
                    textLimit: formField.textLimit,
                    options: finalOptions,
                    heading: replaceCurrentAndPrevious(
                      formField.heading,
                      selectedYear ?? ''
                    ),
                    colSpan: formField.colSpan,
                    restrictFutureDateSelection:
                      formField.restrictFutureDateSelection,
                    fieldName: formField.fieldName,
                    units: formField.units,
                    dateFormat: formField.dateFormat,
                    relatedKey: formField.relatedKey,
                    formula: formField.formula,
                    roundTo: formField.roundTo,
                    disabled: formField.disabled,
                    disabledOn: formField.disabledOn,
                    fileUploadAction: formField.fileUploadAction,
                    fileUploadValidExtensions:
                      formField.fileUploadValidExtensions,
                    fileUploadMaxSize: formField.fileUploadMaxSize,
                    fileUploadAllowMultipleFiles:
                      formField.fileUploadAllowMultipleFiles,
                    conditions: formField.conditions,
                    exclusiveOption: formField.exclusiveOption,
                    requiredOn: formField.requiredOn
                      ? {
                          ...formField.requiredOn,
                          fieldName: formField.requiredOn.fieldName
                        }
                      : undefined
                  }}
                  key={index}
                />
              );
            }
          )}
          table={true}
          buttonText={'Save'}
          setDataSource={setDataSource}
          dataSource={dataSource}
          editId={editId}
          isEdit={isEdit}
        />
      </>
    );
  }

  // Regular table rendering for non-table B
  return (
    <>
      {canAddNew && (
        <Button
          onClick={() => {
            if (setIsEdit && setShowModel) {
              setShowModel(true);
              setIsEdit(false);
            }
          }}
          onKeyDown={(e) => {
            if (setIsEdit && setShowModel && e.key === 'Enter') {
              setShowModel(true);
              setIsEdit(false);
            }
          }}
          style={{float: 'right', marginBottom: '1rem'}}
          type="primary"
        >
          Add New
        </Button>
      )}
      <TableAnt
        className="questionnaire-table"
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'max-content'}}
        tableLayout="auto"
        pagination={{
          pageSize,
          onChange(current) {
            setPage(current);
          }
        }}
      />

      <ModalAddNewToTable
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        field={field}
        data={isEdit ? editData : undefined}
        FormComponent={field?.formList?.formFields.map(
          (formField: any, index: any) => {
            const isRouteFieldForFiltering = isRouteField(formField);

            // Check conditionalVisibility - if field should be hidden, don't render it
            if (
              formField?.conditionalVisibility ||
              formField?.conditions?.shouldShow
            ) {
              return (
                <Form.Item key={index} noStyle shouldUpdate>
                  {({getFieldsValue, getFieldValue}) => {
                    const allFields = getFieldsValue();
                    // Create a getFieldValue wrapper that uses current form data
                    const getFieldValueWrapper = (
                      fieldName: string | string[]
                    ) => {
                      if (typeof fieldName === 'string') {
                        return allFields[fieldName];
                      }
                      // Handle array path like ['field', 'subField']
                      let value = allFields;
                      for (const key of fieldName) {
                        value = value?.[key];
                      }

                      return value;
                    };

                    let shouldShow = true;

                    if (formField?.conditionalVisibility) {
                      shouldShow = isFieldVisible(
                        {
                          conditionalVisibility: formField.conditionalVisibility
                        } as any,
                        getFieldValueWrapper
                      );
                    }

                    if (formField?.conditions?.shouldShow) {
                      shouldShow = evaluateExpression(
                        formField?.conditions?.shouldShow.expression,
                        formField?.conditions?.shouldShow?.formDataDependOn ===
                          'PARENT_FORM'
                          ? FI.getFieldValue([])
                          : getFieldValue([])
                      );
                    }

                    if (!shouldShow) return null;

                    let options = formField.options;

                    if (isRouteFieldForFiltering && goodsFieldNameForRoutes) {
                      const selectedGoodsCategory = getFieldValue(
                        goodsFieldNameForRoutes
                      ) as string | undefined;
                      options = getFilteredRouteOptions(
                        selectedGoodsCategory,
                        formField.options || []
                      );
                    }

                    return (
                      <QuestionnaireSwitch
                        field={{
                          fieldType: formField.type,
                          fieldLabel: replaceCurrentAndPrevious(
                            formField.fieldLabel,
                            selectedYear ?? ''
                          ),
                          textLimit: formField.textLimit,
                          options,
                          heading: replaceCurrentAndPrevious(
                            formField.heading,
                            selectedYear ?? ''
                          ),
                          colSpan: formField.colSpan,
                          restrictFutureDateSelection:
                            formField.restrictFutureDateSelection,
                          fieldName: formField.fieldName,
                          units: formField.units,
                          dateFormat: formField.dateFormat,
                          relatedKey: formField.relatedKey,
                          disabled: formField.disabled,
                          disabledOn: formField.disabledOn,
                          requiredOn: formField.requiredOn,
                          fileUploadAction: formField.fileUploadAction,
                          fileUploadValidExtensions:
                            formField.fileUploadValidExtensions,
                          fileUploadMaxSize: formField.fileUploadMaxSize,
                          fileUploadAllowMultipleFiles:
                            formField.fileUploadAllowMultipleFiles,
                          conditions: formField.conditions
                        }}
                      />
                    );
                  }}
                </Form.Item>
              );
            }

            // If the field has disabledOn or requiredOn logic, wrap it with Form.Item dependencies
            if (
              formField?.disabledOn ||
              formField?.requiredOn ||
              formField?.conditions?.shouldDisable
            ) {
              const dependencies: string[] = [];
              if (formField?.disabledOn) {
                dependencies.push(formField.disabledOn.fieldName);
              }
              if (formField?.requiredOn) {
                dependencies.push(formField.requiredOn.fieldName);
              }

              return (
                <Form.Item
                  key={index}
                  noStyle
                  // Re-render whenever a dependency changes, including when it is
                  // set programmatically (e.g. WYSIWYG setFieldValue or loading a
                  // row for edit), which the `dependencies` prop does not react to
                  shouldUpdate={(prev, cur) =>
                    dependencies.some((dep) => prev?.[dep] !== cur?.[dep])
                  }
                >
                  {({
                    getFieldValue
                  }: {
                    getFieldValue: (name: NamePath) => StoreValue;
                  }) => {
                    // Create a wrapper function for getFieldValue to match the expected signature
                    const getFieldValueWrapper = (
                      fieldName: string | string[]
                    ) => {
                      if (typeof fieldName === 'string') {
                        return getFieldValue(fieldName);
                      }
                      // Handle array path

                      return getFieldValue(fieldName);
                    };

                    let isDisabled = false;
                    if (formField?.disabledOn) {
                      isDisabled = isFieldDisabled(
                        formField,
                        getFieldValueWrapper,
                        formField.rowId,
                        FI.getFieldValue([formField?.disabledOn?.fieldName])
                      );
                    }
                    const isRequired = isFieldRequired(
                      formField,
                      getFieldValueWrapper,
                      formField.rowId
                    );

                    if (formField?.conditions?.shouldDisable) {
                      isDisabled = evaluateExpression(
                        formField?.conditions?.shouldDisable.expression,
                        formField?.conditions?.shouldDisable
                          ?.formDataDependOn === 'PARENT_FORM'
                          ? FI.getFieldValue([])
                          : getFieldValue([])
                      );
                    }

                    let options = formField.options;

                    if (isRouteFieldForFiltering && goodsFieldNameForRoutes) {
                      const selectedGoodsCategory = getFieldValue(
                        goodsFieldNameForRoutes
                      ) as string | undefined;
                      options = getFilteredRouteOptions(
                        selectedGoodsCategory,
                        formField.options || []
                      );
                    }

                    return (
                      <QuestionnaireSwitch
                        field={{
                          fieldType: formField.type,
                          fieldLabel: replaceCurrentAndPrevious(
                            formField.fieldLabel,
                            selectedYear ?? ''
                          ),
                          textLimit: formField.textLimit,
                          options,
                          heading: replaceCurrentAndPrevious(
                            formField.heading,
                            selectedYear ?? ''
                          ),
                          colSpan: formField.colSpan,
                          restrictFutureDateSelection:
                            formField.restrictFutureDateSelection,
                          fieldName: formField.fieldName,
                          units: formField.units,
                          dateFormat: formField.dateFormat,
                          relatedKey: formField.relatedKey,
                          formula: formField.formula,
                          roundTo: formField.roundTo,
                          disabled: isDisabled,
                          disabledOn: formField.disabledOn,
                          requiredOn: formField.requiredOn,
                          isRequired: isRequired,
                          fileUploadAction: formField.fileUploadAction,
                          fileUploadValidExtensions:
                            formField.fileUploadValidExtensions,
                          fileUploadMaxSize: formField.fileUploadMaxSize,
                          fileUploadAllowMultipleFiles:
                            formField.fileUploadAllowMultipleFiles,
                          conditions: formField.conditions
                        }}
                      />
                    );
                  }}
                </Form.Item>
              );
            }

            // For fields without disabledOn logic, render normally
            if (isRouteFieldForFiltering && goodsFieldNameForRoutes) {
              return (
                <RouteFieldWithFilteredOptions
                  key={index}
                  formField={formField}
                  goodsFieldNameForRoutes={goodsFieldNameForRoutes}
                  selectedYear={selectedYear}
                  replaceCurrentAndPrevious={replaceCurrentAndPrevious}
                  getFilteredRouteOptions={getFilteredRouteOptions}
                />
              );
            }

            return (
              <QuestionnaireSwitch
                field={{
                  fieldType: formField.type,
                  fieldLabel: replaceCurrentAndPrevious(
                    formField.fieldLabel,
                    selectedYear ?? ''
                  ),
                  textLimit: formField.textLimit,
                  options: formField.options,
                  heading: replaceCurrentAndPrevious(
                    formField.heading,
                    selectedYear ?? ''
                  ),
                  colSpan: formField.colSpan,
                  restrictFutureDateSelection:
                    formField.restrictFutureDateSelection,
                  fieldName: formField.fieldName,
                  units: formField.units,
                  dateFormat: formField.dateFormat,
                  relatedKey: formField.relatedKey,
                  formula: formField.formula,
                  roundTo: formField.roundTo,
                  disabled: formField.disabled,
                  disabledOn: formField.disabledOn,
                  fileUploadAction: formField.fileUploadAction,
                  fileUploadValidExtensions:
                    formField.fileUploadValidExtensions,
                  fileUploadMaxSize: formField.fileUploadMaxSize,
                  fileUploadAllowMultipleFiles:
                    formField.fileUploadAllowMultipleFiles,
                  conditions: formField.conditions,
                  exclusiveOption: formField.exclusiveOption,
                  requiredOn: formField.requiredOn
                    ? {
                        ...formField.requiredOn,
                        fieldName: formField.requiredOn.fieldName
                      }
                    : undefined
                }}
                key={index}
              />
            );
          }
        )}
        table={true}
        buttonText={'Save'}
        setDataSource={setDataSource}
        dataSource={dataSource}
        editId={editId}
        isEdit={isEdit}
      />
    </>
  );
};

export default QuestionnaireTable;
